type GtagCommand = 'js' | 'config' | 'event' | 'set' | 'consent'

const GA_MEASUREMENT_ID = 'G-JVQE2GT33V'

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (
      command: GtagCommand,
      targetOrDate: string | Date,
      params?: Record<string, unknown>,
    ) => void
  }
}

let loadStarted = false

function ensureDataLayerStub(): void {
  const dataLayer = (window.dataLayer = window.dataLayer || [])
  if (typeof window.gtag === 'function') return
  // The shim MUST push the `arguments` object itself — never an array literal.
  // gtag.js/GTM branches on Object.prototype.toString.call(entry): an
  // "[object Arguments]" entry is routed to the gtag command processor, while
  // an "[object Array]" entry falls through to the legacy dotted-command path
  // (data-layer-helper processCommand_), finds no matching method, and is
  // silently discarded. The old shim pushed [command, target, params] arrays,
  // so gtag.js loaded, drained the queue, emitted gtm.dom/gtm.load and logged
  // no errors — but never saw a `config` or `event` command, and therefore
  // never issued a single /g/collect request. See GA_DEBUG_HANDOFF.md.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    dataLayer.push(arguments)
  }
}

// Events that show a real visitor is on the page. GA loads on the first one,
// so gtag.js never runs while the page is still loading and rendering.
const INTERACTION_EVENTS = [
  'pointerdown',
  'pointermove',
  'keydown',
  'scroll',
  'touchstart',
  'wheel',
] as const

// Visitors who read without interacting are still counted, this long after
// the load event — late enough to stay out of the page-load window that
// Lighthouse measures.
const FALLBACK_DELAY_MS = 10_000

/** Load GA4 on the first interaction (or 10s after load) so gtag stays out of TBT. */
export function initAnalytics(): void {
  if (typeof window === 'undefined' || loadStarted) return
  loadStarted = true
  ensureDataLayerStub()

  let fallbackTimer: number | undefined

  const load = () => {
    INTERACTION_EVENTS.forEach((type) =>
      window.removeEventListener(type, load, true),
    )
    window.clearTimeout(fallbackTimer)
    ensureDataLayerStub()
    if (document.querySelector(`script[data-ga-id="${GA_MEASUREMENT_ID}"]`)) {
      return
    }
    // Queue js/config into dataLayer *before* the library loads (Google's
    // documented pattern) — gtag.js drains the queue on load.
    window.gtag?.('js', new Date())
    window.gtag?.('config', GA_MEASUREMENT_ID)
    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
    script.dataset.gaId = GA_MEASUREMENT_ID
    document.head.appendChild(script)
  }

  INTERACTION_EVENTS.forEach((type) =>
    window.addEventListener(type, load, { capture: true, passive: true }),
  )

  const startFallback = () => {
    fallbackTimer = window.setTimeout(load, FALLBACK_DELAY_MS)
  }
  if (document.readyState === 'complete') {
    startFallback()
  } else {
    window.addEventListener('load', startFallback, { once: true })
  }
}

function trackEvent(
  name: string,
  params?: Record<string, string | number | boolean>,
): void {
  ensureDataLayerStub()
  if (typeof window.gtag !== 'function') return
  window.gtag('event', name, params)
}

/** GA4 recommended lead event — mark as a key event in Admin → Events. */
export function trackGenerateLead(source: string): void {
  trackEvent('generate_lead', {
    lead_source: source,
  })
}

/** Footer email signup — mark as a key event in Admin → Events. */
export function trackNewsletterSubscribe(): void {
  trackEvent('newsletter_subscribe', {
    method: 'footer',
  })
}
